import React, { useContext } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Alert, Empty, Flex, Select, Spin } from 'antd';
import dayjs from 'dayjs';

import UbiquitiHostISPMetricsContext from '@totem/components/ubiquiti/hosts/details/ispMetrics/ubiquitiHostISPMetricsContext';
import { UbiquitiISPMetric } from '@totem/components/ubiquiti/types';

const baseOptions: ApexCharts.ApexOptions = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
  },
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: 2 },
  xaxis: {
    type: 'datetime',
    // ApexCharts formats datetime axes in UTC by default regardless of the
    // browser's timezone — format explicitly with dayjs (which renders in
    // local time unless the UTC plugin is used) rather than relying on the
    // datetimeUTC flag, whose behavior has varied across versions.
    labels: {
      formatter: (value) => dayjs(Number(value)).format('MMM D, h:mm A'),
    },
  },
  tooltip: {
    x: {
      formatter: (value) => dayjs(Number(value)).format('MMM D, YYYY h:mm A'),
    },
  },
  legend: { position: 'top' },
};

const buildSeries = (
  metric: UbiquitiISPMetric,
  name: string,
  select: (wan: UbiquitiISPMetric['periods'][number]['data']['wan']) => number,
) => [
  {
    name,
    data: metric.periods.map((period) => ({
      x: dayjs(period.metricTime).valueOf(),
      y: select(period.data.wan),
    })),
  },
];

const UbiquitiHostISPMetricsChart = () => {
  const { loading, error, metrics, duration, setDuration } = useContext(
    UbiquitiHostISPMetricsContext,
  );

  if (error) {
    return (
      <Alert
        type="warning"
        showIcon
        message="ISP metrics unavailable"
        description="No ISP metric data could be found for this host."
      />
    );
  }

  if (loading) {
    return <Spin />;
  }

  if (metrics.length === 0) {
    return <Empty description="No ISP metrics available for this host." />;
  }

  return (
    <>
      {metrics.map((metric) => {
        const firstWan = metric.periods[0]?.data.wan;
        const ispLabel =
          firstWan && firstWan.ispName ? firstWan.ispName : 'ISP';

        return (
          <div
            key={`${metric.hostId}-${metric.siteId}`}
            style={{ marginBottom: 32 }}
          >
            <Flex justify={'space-between'} align={'center'}>
              <div>
                <h4>{ispLabel}</h4>
              </div>
              <div>
                <Flex justify={'flex-end'} align={'center'}>
                  <div>Duration:</div>
                  <div>
                    <Select
                      value={duration}
                      options={[
                        { value: '1', label: '24 Hours' },
                        { value: '7', label: '7 Days' },
                        { value: '30', label: '30 Days' },
                      ]}
                      onChange={(newValue) => setDuration(newValue)}
                    />
                  </div>
                </Flex>
              </div>
            </Flex>

            <ReactApexChart
              options={{
                ...baseOptions,
                title: { text: 'Throughput (Kbps)' },
              }}
              series={[
                ...buildSeries(
                  metric,
                  'Download (Kbps)',
                  (wan) => wan.download_kbps,
                ),
                ...buildSeries(
                  metric,
                  'Upload (Kbps)',
                  (wan) => wan.upload_kbps,
                ),
              ]}
              type="line"
              height={250}
            />
            <ReactApexChart
              options={{
                ...baseOptions,
                title: { text: 'Latency (ms)' },
              }}
              series={[
                ...buildSeries(
                  metric,
                  'Avg Latency (ms)',
                  (wan) => wan.avgLatency,
                ),
                ...buildSeries(
                  metric,
                  'Max Latency (ms)',
                  (wan) => wan.maxLatency,
                ),
              ]}
              type="line"
              height={200}
            />
            <ReactApexChart
              options={{
                ...baseOptions,
                title: { text: 'Packet Loss (%)' },
              }}
              series={buildSeries(
                metric,
                'Packet Loss (%)',
                (wan) => wan.packetLoss,
              )}
              type="line"
              height={150}
            />
          </div>
        );
      })}
    </>
  );
};

export default UbiquitiHostISPMetricsChart;
